import { useEffect, useState } from "react";
import { BIN_ID, Sidebar } from "./components/Sidebar";
import { ThreadView } from "./components/ThreadView";
import { BinView } from "./components/BinView";
import { LoginPage } from "./components/LoginPage";
import type { Accent, Thread } from "./lib/types";
import { api } from "./lib/api";
import { ConfirmProvider, useConfirm } from "./lib/confirm";
import { ToastProvider } from "./lib/toast";

const LAST_ACTIVE_KEY = "workit:lastActiveThread";

export default function App() {
  // Providers wrap the entire app once so useConfirm() / useToast() work
  // from anywhere — sidebar, modal, bin, etc.
  return (
    <ToastProvider>
      <ConfirmProvider>
        <AppRoot />
      </ConfirmProvider>
    </ToastProvider>
  );
}

function AppRoot() {
  // Auth state is intentionally in-memory only — every page load asks for the
  // PIN. We do not write to localStorage/sessionStorage so a reload is a fresh
  // sign-in.
  const [authed, setAuthed] = useState<boolean>(false);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [binCount, setBinCount] = useState(0);

  // The Bin button count is refreshed (a) on first load, (b) whenever the
  // user opens/closes the Bin view, and (c) when a card is deleted from
  // a thread (handler in ThreadView calls refreshBinCount via callback).
  const refreshBinCount = async () => {
    try {
      const list = await api.listBin();
      setBinCount(list.length);
    } catch {
      // Non-fatal — sidebar just won't show the count.
    }
  };

  useEffect(() => {
    if (!authed) return;
    api.listThreads().then((list) => {
      setThreads(list);
      const remembered = localStorage.getItem(LAST_ACTIVE_KEY);
      if (remembered === BIN_ID) {
        setActiveId(BIN_ID);
      } else if (remembered && list.some((t) => t.id === remembered)) {
        setActiveId(remembered);
      } else if (list.length > 0) {
        setActiveId(list[0].id);
      }
      setLoaded(true);
    });
    refreshBinCount();
  }, [authed]);

  useEffect(() => {
    if (activeId) localStorage.setItem(LAST_ACTIVE_KEY, activeId);
  }, [activeId]);

  // Global "N" hotkey for new thread (only when not focused in input).
  useEffect(() => {
    if (!authed) return;
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement) {
        const tag = e.target.tagName.toLowerCase();
        if (tag === "input" || tag === "textarea" || e.target.isContentEditable) return;
      }
      if ((e.key === "N" && e.shiftKey) || (e.key === "n" && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        handleCreate("Untitled thread", "lanes", "lavender");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const handleCreate = async (name: string, kind: "lanes" | "flat", accent: Accent) => {
    const t = await api.createThread(name, kind, accent);
    setThreads((prev) => [t, ...prev]);
    setActiveId(t.id);
  };

  const handleRename = async (id: string, name: string) => {
    setThreads((prev) => prev.map((t) => (t.id === id ? { ...t, name } : t)));
    await api.updateThread(id, { name }).catch(console.error);
  };

  const handleAccent = async (id: string, accent: Accent) => {
    setThreads((prev) => prev.map((t) => (t.id === id ? { ...t, accent } : t)));
    await api.updateThread(id, { accent }).catch(console.error);
  };

  const handlePin = async (id: string, pinned: boolean) => {
    // Optimistically flip pinned and re-sort the in-memory list so the row
    // visually jumps to the correct section (pinned first).
    setThreads((prev) => {
      const next = prev.map((t) =>
        t.id === id ? { ...t, pinned: pinned ? 1 : 0 } : t
      );
      next.sort((a, b) => {
        if (a.pinned !== b.pinned) return b.pinned - a.pinned;
        return a.sort_order - b.sort_order;
      });
      return next;
    });
    await api.updateThread(id, { pinned }).catch(console.error);
  };

  const handleReorder = async (orderedIds: string[]) => {
    // Renumber sort_order according to the new ID order. The pinned bit
    // already lives on each thread; we only touch sort_order. The server
    // query orders by `pinned DESC, sort_order ASC` so two threads sharing
    // sort_order is still visually fine (one is pinned, one isn't).
    const indexOf = new Map(orderedIds.map((id, i) => [id, i]));
    setThreads((prev) => {
      const next = prev
        .map((t) => ({ ...t, sort_order: indexOf.get(t.id) ?? t.sort_order }))
        .sort((a, b) => {
          if (a.pinned !== b.pinned) return b.pinned - a.pinned;
          return a.sort_order - b.sort_order;
        });
      return next;
    });
    const items = orderedIds.map((id, i) => ({ id, sort_order: i }));
    await api.reorder("thread", items).catch(console.error);
  };

  const handleArchive = async (id: string) => {
    setThreads((prev) => prev.filter((t) => t.id !== id));
    if (activeId === id) {
      const remaining = threads.filter((t) => t.id !== id);
      setActiveId(remaining[0]?.id ?? null);
    }
    await api.updateThread(id, { archived: 1 }).catch(console.error);
  };

  const confirm = useConfirm();

  const handleDelete = async (id: string) => {
    const thread = threads.find((t) => t.id === id);
    const ok = await confirm({
      title: `delete "${thread?.name ?? "this thread"}"?`,
      body:
        "all lanes and cards inside this thread will be permanently deleted. this can't be undone.",
      confirmLabel: "delete thread",
      danger: true,
    });
    if (!ok) return;
    setThreads((prev) => prev.filter((t) => t.id !== id));
    if (activeId === id) {
      const remaining = threads.filter((t) => t.id !== id);
      setActiveId(remaining[0]?.id ?? null);
    }
    await api.deleteThread(id).catch(console.error);
  };

  const handleAuth = () => {
    setAuthed(true);
  };

  if (!authed) {
    return <LoginPage onAuth={handleAuth} />;
  }

  const active = threads.find((t) => t.id === activeId) ?? null;
  const isBin = activeId === BIN_ID;

  return (
    <div className="relative flex h-screen w-screen overflow-hidden bg-canvas">
      <Sidebar
        threads={threads}
        activeId={activeId}
        binCount={binCount}
        onSelect={setActiveId}
        onCreate={handleCreate}
        onRename={handleRename}
        onChangeAccent={handleAccent}
        onTogglePin={handlePin}
        onReorder={handleReorder}
        onArchive={handleArchive}
        onDelete={handleDelete}
      />
      <main className="relative flex h-full flex-1 flex-col overflow-hidden">
        {!loaded ? (
          <div className="flex h-full items-center justify-center text-sm text-ink-mute">
            loading...
          </div>
        ) : isBin ? (
          <BinView onChanged={refreshBinCount} />
        ) : active ? (
          <ThreadView
            key={active.id}
            thread={active}
            onThreadUpdated={(t) =>
              setThreads((prev) => prev.map((x) => (x.id === t.id ? t : x)))
            }
            onCardDeleted={refreshBinCount}
          />
        ) : (
          <EmptyState onCreate={handleCreate} />
        )}
      </main>
    </div>
  );
}

function EmptyState({
  onCreate,
}: {
  onCreate: (name: string, kind: "lanes" | "flat", accent: Accent) => void;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-8 text-center">
      <div className="max-w-md">
        <h2 className="text-3xl font-semibold tracking-tight text-ink">
          a quiet place to think.
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-ink-soft">
          threads hold your work. lanes shape it. cards capture it.
          you decide the rest.
        </p>
        <button
          onClick={() => onCreate("My first thread", "lanes", "lavender")}
          className="mt-8 inline-flex items-center gap-1.5 rounded-lg bg-lavender-100 px-4 py-2 text-sm font-medium text-lavender-400 transition-colors hover:bg-lavender-200/70"
        >
          start a thread
        </button>
      </div>
    </div>
  );
}
